import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Row,
  Typography,
  Statistic,
  Divider,
  Progress,
  Tag,
  Badge,
  List,
  Radio,
  Tabs,
  Table,
  Tooltip,
  Select,
} from "antd";
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileDoneOutlined,
  ShoppingOutlined,
  EuroOutlined,
  PicCenterOutlined,
  ShopOutlined,
  UserOutlined,
  GiftOutlined,
  LikeOutlined,
  CommentOutlined,
  ThunderboltOutlined,
  StarOutlined,
  TrophyOutlined,
  TeamOutlined,
  MoneyCollectOutlined,
  HeartOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import _ from "lodash";
import RewardAnalysisCard from "../components/chart/EChart";
import PostsAnalysisCard from "../components/PostsAnalysis";
import CampaignsTable from "./CampaignTable";
import CampaignPostsEvolutionChart from "../components/CampaignPostsEvolutionChart";

const { Title, Text } = Typography;
const { Option } = Select;

export function extractLikesFromString(str) {
  if (!str) return 0;

  // Remove non-numeric characters except dot and comma
  const cleanStr = str
    .toUpperCase()
    .replace(/[^0-9KM,.]/g, "")
    .trim();

  if (cleanStr.includes("K")) {
    return Math.round(
      parseFloat(cleanStr.replace("K", "").replace(",", "")) * 1000
    );
  }

  if (cleanStr.includes("M")) {
    return Math.round(
      parseFloat(cleanStr.replace("M", "").replace(",", "")) * 1000000
    );
  }

  // Handle normal numbers with commas (e.g., "1,749")
  return parseInt(cleanStr.replace(/,/g, ""), 10) || 0;
}

export const formatNumber = (number) => {
  return new Intl.NumberFormat("fr-FR", {
    notation: "compact",
    compactDisplay: "short",
  }).format(number);
};

// View modes for the chart
export const viewModes = {
  MONTHLY: "monthly",
  YEARLY: "yearly",
  DAILY: "daily",
  STATUS: "status",
};

function Home() {
  const [invoices, setInvoices] = useState([]);
  const [Etablissement, setEtablissement] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [users, setusers] = useState([]);
  const [campaigns, setCampaigns] = useState([]); // New state for campaigns
  const [selectedCampaign, setSelectedCampaign] = useState(null); // Selected campaign for the chart
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(viewModes.MONTHLY);
  const [selectedYear, setSelectedYear] = useState(moment().year());

  const [viewMode2, setViewMode2] = useState(viewModes.MONTHLY);
  const [selectedYear2, setSelectedYear2] = useState(moment().year());
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await axios.get("https://trend-me.com/api/Post");
        setInvoices(response.data);
        const responseEtab = await axios.get(
          "https://trend-me.com/api/Etablissement"
        );
        setEtablissement(responseEtab.data);
        const responseClients = await axios.get(
          "https://trend-me.com/api/user"
        );
        setusers(responseClients.data);

        const rewards = await fetch("https://trend-me.com/rewards");
        const data = await rewards.json();
        setRewards(data);

        // Fetch campaigns if you have an API endpoint for them
        // const responseCampaigns = await axios.get("https://trend-me.com/api/campaigns");
        // setCampaigns(responseCampaigns.data);

        // If campaigns are embedded in posts or establishments, extract them here
        // Example: extracting unique campaigns from posts
        const uniqueCampaigns = _.uniqBy(
          response.data
            .filter((post) => post.selectedCampaignName)
            .map((post) => ({
              name: post.selectedCampaignName,
              id: post.selectedCampaignName, // assuming name is unique
              // You might need to extract dates from posts or have them in a separate API
              dateDebut: post.campaignStartDate, // adjust based on your data structure
              dateFin: post.campaignEndDate, // adjust based on your data structure
            })),
          "name"
        );
        setCampaigns(uniqueCampaigns);

        // Set the first campaign as selected by default
        if (uniqueCampaigns.length > 0) {
          setSelectedCampaign(uniqueCampaigns[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchInvoicesByEttab = async () => {
      try {
        const response = await axios.get(
          "https://trend-me.com/api/Post/" + user?.IdEtab
        );

        const rewards = await fetch(
          "https://trend-me.com/rewards/ettab/" + user?.IdEtab
        );
        const data = await rewards.json();
        setRewards(data);

        const responseClients = await axios.get(
          "https://trend-me.com/api/user"
        );
        setusers(responseClients.data);

        setInvoices(response.data);
        setEtablissement([{ _id: user?.IdEtab }]);

        // Extract campaigns from establishment posts
        const uniqueCampaigns = _.uniqBy(
          response.data
            .filter((post) => post.selectedCampaignName)
            .map((post) => ({
              name: post.selectedCampaignName,
              id: post.selectedCampaignName,
              dateDebut: post.campaignStartDate,
              dateFin: post.campaignEndDate,
            })),
          "name"
        );
        setCampaigns(uniqueCampaigns);

        if (uniqueCampaigns.length > 0) {
          setSelectedCampaign(uniqueCampaigns[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.type === "admin") {
      fetchInvoices();
    } else {
      fetchInvoicesByEttab();
    }
  }, []);

  // Calculate total followers from users with posts
  const totalFollowers = (() => {
    // Get unique users who have posted
    const usersWithPosts = _.uniqBy(
      invoices.map((inv) => inv.userId?._id).filter(Boolean),
      (id) => id
    );

    // Sum their followers
    return _.sum(
      users
        .filter(
          (user) => usersWithPosts.includes(user._id) && user.nbFollowersInsta
        )
        .map((user) => extractLikesFromString(user.nbFollowersInsta))
    );
  })();

  // Calculate average engagement rate
  const calculateEngagementRate = () => {
    const posts = invoices.filter((post) => post.likes && post.comments);
    if (posts.length === 0) return 0;

    const totalEngagement = posts.reduce((sum, post) => {
      const likes = extractLikesFromString(post.likes);
      const comments = extractLikesFromString(post.comments);
      return sum + likes + comments;
    }, 0);

    // Calculate average engagement rate (Likes + Comments)/followers * 100
    const avgEngagement = (totalEngagement / totalFollowers) * 100;
    return avgEngagement.toFixed(2);
  };

  // Calculate cost per 1000 followers
  const calculateCostPer1000Followers = () => {
    // Real cost of distributed rewards
    const realCost = 230; // From your Excel data

    if (totalFollowers === 0) return 0;

    return ((realCost / totalFollowers) * 1000).toFixed(2);
  };

  // Calculate total likes within 7 days
  const calculateTotalLikesIn7Days = () => {
    const sevenDaysAgo = moment().subtract(7, "days");
    const recentPosts = invoices.filter(
      (post) =>
        post.likes &&
        moment(post.creationDate, "DD MMM YYYY [à] HH:mm").isAfter(sevenDaysAgo)
    );

    const totalLikes = recentPosts.reduce((sum, post) => {
      return sum + extractLikesFromString(post.likes);
    }, 0);

    return totalLikes;
  };

  // Calculate total comments within 7 days
  const calculateTotalCommentsIn7Days = () => {
    const sevenDaysAgo = moment().subtract(7, "days");

    const recentPosts = invoices.filter(
      (post) =>
        post.comments &&
        moment(post.creationDate, "DD MMM YYYY [à] HH:mm").isAfter(sevenDaysAgo)
    );

    const totalComments = recentPosts.reduce((sum, post) => {
      return sum + extractLikesFromString(post.comments);
    }, 0);

    return totalComments;
  };

  // New stats based on Excel data
  const stats = [
    {
      title: "Montant Total Récompenses Distribué",
      value: rewards.reduce((sum, reward) => {
        return sum + Number(reward?.value);
      }, 0),
      icon: <DollarOutlined style={{ fontSize: 24 }} />,
      color: "#3B82F6",
      tooltip: "Montant total des récompenses distribuées en valeur faciale",
    },
    {
      title: "Coût Réel Total Récompense",
      value: rewards.reduce((sum, reward) => {
        return sum + Number(reward?.cout);
      }, 0),
      icon: <EuroOutlined style={{ fontSize: 24 }} />,
      color: "#10B981",
      tooltip: "Coût réel total des récompenses pour l'établissement",
    },
    {
      title: "Total Postes",
      value: invoices.length,
      subValueLikes: `${formatNumber(
        invoices.reduce(
          (sum, post) => sum + extractLikesFromString(post.likes || "0"),
          0
        )
      )} J'aime`,
      subValueComments: `${formatNumber(
        invoices.reduce(
          (sum, post) => sum + extractLikesFromString(post.comments || "0"),
          0
        )
      )} Commentaires`,
      icon: <PicCenterOutlined style={{ fontSize: 24 }} />,
      color: "#8B5CF6",
      tooltip: "Nombre total de posts effectués",
    },
    {
      title: "Total Like (à 7j)",
      value: formatNumber(calculateTotalLikesIn7Days()),
      icon: <HeartOutlined style={{ fontSize: 24 }} />,
      color: "#F43F5E",
      tooltip: "Total des likes reçus sur les posts des 7 derniers jours",
    },
    {
      title: "Total Comm (à 7 j)",
      value: formatNumber(calculateTotalCommentsIn7Days()),
      icon: <MessageOutlined style={{ fontSize: 24 }} />,
      color: "#06B6D4",
      tooltip:
        "Total des commentaires reçus sur les posts des 7 derniers jours",
    },
    {
      title: "Taux d'engagement moyen",
      value: `${calculateEngagementRate()}%`,
      icon: <ThunderboltOutlined style={{ fontSize: 24 }} />,
      color: "#F59E0B",
      tooltip:
        "Taux d'engagement Instagram = (Likes + Commentaires)/abonnés (moyenne) x 100 %",
    },
    {
      title: "Nombre de Follower Total",
      value: formatNumber(totalFollowers),
      icon: <TeamOutlined style={{ fontSize: 24 }} />,
      color: "#EC4899",
      tooltip: "Cumul du nombre de follower total des utilisateurs ayant posté",
    },
    {
      title: "Nombre Moyen de follower par client",
      value: formatNumber(
        totalFollowers /
          (_.uniqBy(invoices.map((inv) => inv.userId?._id).filter(Boolean))
            .length || 1)
      ),
      icon: <UserOutlined style={{ fontSize: 24 }} />,
      color: "#6366F1",
      tooltip: "Nombre moyen de followers par utilisateur ayant posté",
    },
    {
      title: "Nombre de récompenses distribuées",
      value: rewards?.length,
      icon: <GiftOutlined style={{ fontSize: 24 }} />,
      color: "#F97316",
      tooltip: "Total des récompenses (utilisées et non utilisées)",
    },
    {
      title: "Nombre de récompenses utilisées",
      value: rewards?.filter((el) => el.status === "used")?.length,
      icon: <TrophyOutlined style={{ fontSize: 24 }} />,
      color: "#14B8A6",
      tooltip: "Nombre de récompenses réclamées par les utilisateurs",
    },
    {
      title: "Coût pour 1000 Followers",
      value: `${calculateCostPer1000Followers()} €`,
      icon: <MoneyCollectOutlined style={{ fontSize: 24 }} />,
      color: "#EF4444",
      tooltip: "(Coût réel dépensé / cumul des followers total) * 1000",
    },
  ];

  // Year selection dropdown for the chart
  const availableYears = [
    ...new Set(
      invoices.map((inv) =>
        moment(inv.creationDate, "DD MMM YYYY [à] HH:mm").year()
      )
    ),
  ].sort((a, b) => b - a);

  return (
    <div className="layout-content">
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={12} lg={6} xl={6} key={index}>
            <Tooltip title={stat.tooltip}>
              <Card
                bordered={false}
                loading={loading}
                hoverable
                style={{
                  borderRadius: 16,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                  transition: "transform 0.3s ease",
                  height: "100%",
                }}
                bodyStyle={{
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: `${stat.color}20`,
                      borderRadius: "50%",
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(stat.icon, {
                      style: {
                        fontSize: 24,
                        color: stat.color,
                      },
                    })}
                  </div>
                  <div>
                    <Text
                      style={{
                        fontSize: 14,
                        color: "#8c8c8c",
                        fontWeight: 500,
                      }}
                    >
                      {stat.title}
                    </Text>
                    <Title
                      level={3}
                      style={{ margin: 0, fontWeight: 600, color: "#1f1f1f" }}
                    >
                      {stat.value}
                    </Title>
                  </div>
                </div>

                {(stat.subValueLikes || stat.subValueComments) && (
                  <div
                    style={{
                      borderTop: "1px dashed #f0f0f0",
                      paddingTop: 12,
                      marginTop: "auto",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 12,
                        justifyContent: "center",
                      }}
                    >
                      {stat.subValueLikes && (
                        <Text style={{ fontSize: 13, color: stat.color }}>
                          ❤️ {stat.subValueLikes}
                        </Text>
                      )}
                      {stat.subValueComments && (
                        <Text style={{ fontSize: 13, color: stat.color }}>
                          💬 {stat.subValueComments}
                        </Text>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </Tooltip>
          </Col>
        ))}
      </Row>

      <CampaignsTable />

      {/* Campaign Selection and Posts Evolution Chart */}
      <Row gutter={24} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ marginRight: 16 }}>
                Sélectionner une campagne:
              </Text>
              <Select
                style={{ width: 250 }}
                value={selectedCampaign?.name}
                onChange={(value) => {
                  const campaign = campaigns.find((c) => c.name === value);
                  setSelectedCampaign(campaign);
                }}
                placeholder="Choisir une campagne"
                disabled={loading || campaigns.length === 0}
              >
                {campaigns.map((campaign) => (
                  <Option key={campaign.id} value={campaign.name}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Campaign Posts Evolution Chart */}
            <CampaignPostsEvolutionChart
              invoices={invoices}
              users={users}
              selectedCampaign={selectedCampaign}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      {/* Uncomment these if you want to include the other charts */}
      {/*
      <Divider />

      <RewardAnalysisCard
        invoices={invoices}
        loading={loading}
        availableYears={availableYears}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        setViewMode={setViewMode}
        viewMode={viewMode}
      />

      <PostsAnalysisCard
        posts={invoices}
        loading={loading}
        availableYears={availableYears}
        selectedYear={selectedYear2}
        setSelectedYear={setSelectedYear2}
        setViewMode={setViewMode2}
        viewMode={viewMode2}
      />
      */}
    </div>
  );
}

export default Home;
